import {
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from "@mui/material";
import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import { useNavigate } from "react-router";
import CreateContentVersionPopup from "./createContentVersion";
import DeleteModal from "../../UI/atoms/Popup/DeleteModal";
import {
    LOCAL_PST_DATE_CONVERSION,
    MILLIS_TO_LOCALE,
    MILLIS_TO_LOCALE_DATE_TIME_FORMAT_POPUP,
    MILLIS_TO_LOCALE_DIGIT_FORMAT,
    dateDifference,
} from "../../../utilities/helpers/formatUnix";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { DRAFT_STATUS, LIVE, SCHEDULED_STATUS } from "../LandingPage/constants";
import GeneralModal from "../../UI/atoms/Popup/GeneralModal";
import CloneContentVersion from "./CloneContentVersion";
import { useCountdown } from "../../UI/hooks/UseCountDown";
import CountdownTimer from "./CountDownTimer";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CancelIcon from "@mui/icons-material/Cancel";

dayjs.extend(utc);
dayjs.extend(timezone);

import moment from "moment-timezone";
import {
    ACTION,
    PERMISSIONS,
    usePermissions,
} from "../../../utils/permissions";

const ContentCurationVersionTable = forwardRef(
    ({ status, showActionButton, refreshTables }: any, ref: any) => {
        const timezoneCode = moment.tz(moment.tz.guess()).zoneAbbr();
        // hooks
        const navigate = useNavigate();
        const [loader, setLoader] = useState(false);
        const [versionPopup, setVersionPopup] = useState(false);
        const { enqueueSnackbar } = useSnackbar();
        const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
        const [selectedPage, setSelectedPage] = useState<any>(null);
        const [liveVersionList, setLiveVersionList] = useState<any>([]);
        const [draftVersionList, setDraftVersionList] = useState<any>([]);
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);
        const [totalCount, setTotalCount] = useState(0);
        const [skipKey, setSkipKey] = useState("");

        const [skipKeyQueue, setskipKeyQueue] = useState<any>("");
        const [masterInformation, setMasterInformation] = useState<any>({});
        const [hoverId, setHoverId] = useState<any>(null);
        const [showCancelPopup, setShowCancelPopup] = useState(false);
        const [cancelLoader, setCancelLoader] = useState(false);
        const { hasPermission } = usePermissions();

        useImperativeHandle(
            ref,
            () => ({
                getOtherPages,
            }),
            []
        );

        useEffect(() => {
            getOtherPages();
            getMasterInfo();
        }, []);

        const resetTableMeta = () => {
            setskipKeyQueue([]);
            setPage(0);
        };

        // call API for getting the master information
        const getMasterInfo = async () => {
            try {
                const materInformation = await axios.get(
                    `${process.env.BASE_URL}api/v1/cms/page/master`
                );
                if (materInformation.data?.success) {
                    let masterKeyValue: any = {};
                    const pageTemplate =
                        materInformation.data?.data?.pageTemplate;
                    pageTemplate.map(
                        (template: any) =>
                            (masterKeyValue[template.key] = template.label)
                    );
                    setMasterInformation(masterKeyValue);
                }
            } catch (error) {
                console.log("Error in getting master information", error);
            }
        };

        const getOtherPages = async () => {
            resetTableMeta();
            const pages = await getPagesDraft(status, rowsPerPage);
            setTotalCount(pages.totalCount);
            setDraftVersionList([...pages.list]);
        };

        const getPagesDraft = async (
            status: any,
            limit: number | string = rowsPerPage || 0,
            skip: string = skipKey || "",
            skipQueue: any[] | null = skipKeyQueue
        ) => {
            const pages = await getPagesList(status, limit, skip);
            const list = pages?.list;
            list.length &&
                pushKey(
                    `${list[list.length - 1]?.id}__${
                        list[list.length - 1]?.lastModified
                    }`,
                    skipQueue
                );
            return pages;
        };

        const getPagesList = async (
            status: string,
            limit: number | string,
            skip: string = ""
        ) => {
            try {
                setLoader(true);
                const saveVersion = await axios.get(
                    `${process.env.BASE_URL}api/v1/cms/page/filter?status=${status}&skip=${skip}&limit=${limit}`
                );
                return saveVersion.data?.data;
            } catch (error: any) {
                console.log(
                    "Error in getting master information",
                    error.response
                );
                enqueueSnackbar(error.response?.data?.message, {
                    variant: "warning",
                });
            } finally {
                setLoader(false);
            }
        };

        const handleClosePopup = () => {
            setVersionPopup(false);
            setSelectedPage(null);
        };

        const openDeleteModal = (page: any) => {
            setSelectedPage(page);
            setIsDeleteModalOpen(true);
        };

        const closeDeleteModal = () => {
            setIsDeleteModalOpen(false);
            setSelectedPage(null);
        };

        const closeCancelModal = () => {
            setShowCancelPopup(false);
            setSelectedPage(null);
        };

        const deleteTheObject = async () => {
            try {
                setLoader(true);
                const deleteRail = await axios.delete(
                    `${process.env.BASE_URL}api/v1/cms/page/${
                        selectedPage.id
                    }?status=${status}&eventArn=${
                        selectedPage.publishArn || ""
                    }`
                );
                if (deleteRail?.data?.success) {
                    enqueueSnackbar(deleteRail?.data?.message, {
                        variant: "success",
                    });
                    getOtherPages();
                    closeDeleteModal();
                } else {
                    enqueueSnackbar(deleteRail?.data?.message, {
                        variant: "error",
                    });
                }
            } catch (error: any) {
                enqueueSnackbar(error?.response?.data?.message, {
                    variant: "error",
                });
                console.log("Error in getting master information", error);
            } finally {
                setLoader(false);
            }
        };

        const handleAddVersion = async (value: any) => {
            console.log(value);
            const payload = {
                versionName: value.versionName,
                canonicalName: value.canonicalName,
                template: value.template,
                publishStatus: "draft",
            };
            await createVersionAPI(payload);
        };

        const navigateToList = (id: string) => {
            navigate("/content-curation-list?pageName=" + id);
        };

        const navigateToPageDetails = (id: string) => {
            navigate("/content-curation-list?pageName=" + id);
        };

        const createVersionAPI = async (value: any) => {
            try {
                setLoader(true);
                const saveVersion = await axios.post(
                    `${process.env.BASE_URL}api/v1/cms/page/versions`,
                    value
                );
                if (saveVersion?.data?.success) {
                    enqueueSnackbar(saveVersion?.data?.message, {
                        variant: "success",
                    });
                    navigateToList(saveVersion?.data?.data?.id);
                    handleClosePopup();
                } else {
                    enqueueSnackbar(saveVersion?.data?.message, {
                        variant: "error",
                    });
                }
            } catch (error: any) {
                console.log(
                    "Error in getting master information",
                    error.response
                );
                enqueueSnackbar(error.response?.data?.message, {
                    variant: "warning",
                });
            } finally {
                setLoader(false);
            }
        };

        const handlePageConfiguration = (id: string) => {
            navigateToPageDetails(id);
        };

        const handleCancelPage = (page: any) => {
            setShowCancelPopup(true);
            setSelectedPage(page);
        };

        const handleMoveToDraft = () => {
            // Make API Call
            const params = {
                publishArn: selectedPage?.publishArn,
                versionName: selectedPage?.versionName,
                template: selectedPage?.template,
                id: selectedPage?.id,
            };
            unscheduleVersionAPI(params);
        };

        const unscheduleVersionAPI = async (payload: any) => {
            try {
                setCancelLoader(true);
                const unschedule = await axios.put(
                    `${process.env.BASE_URL}api/v1/cms/page/unschedule`,
                    payload
                );
                if (unschedule?.data?.success) {
                    refreshTables();
                    enqueueSnackbar(unschedule?.data?.message, {
                        variant: "success",
                    });
                    closeCancelModal();
                } else {
                    enqueueSnackbar(unschedule?.data?.message, {
                        variant: "error",
                    });
                }
            } catch (error: any) {
                console.log("Error in unscheduleVersionAPI", error.response);
                enqueueSnackbar(error.response?.data?.message, {
                    variant: "warning",
                });
            } finally {
                setCancelLoader(false);
            }
        };

        const tableRowRender = () => {
            return (
                <TableRow>
                    <TableCell width={150}>
                        <h3>Name</h3>
                    </TableCell>
                    <TableCell width={200}>
                        <h3>Page</h3>
                    </TableCell>
                    <TableCell width={100}>
                        <h3>Status</h3>
                    </TableCell>
                    <TableCell width={200}>
                        <h3>Date</h3>
                    </TableCell>
                    <TableCell align="center" width={150}>
                        <h3>Actions</h3>
                    </TableCell>
                </TableRow>
            );
        };

        const handleChangePage = async (event: unknown, newPage: number) => {
            let skipKeyValue = "";
            let queue;
            if (newPage < page) {
                queue = popTwice();
                setskipKeyQueue([...queue]);
                skipKeyValue = queue[queue.length - 1] || "";
                console.log(skipKeyQueue);
            } else {
                skipKeyValue = skipKeyQueue[skipKeyQueue.length - 1];
            }
            console.log("newPage", newPage);
            setPage(newPage);
            const pages = await getPagesDraft(
                status,
                rowsPerPage,
                skipKeyValue,
                queue
            );
            setDraftVersionList([...pages.list]);
            setTotalCount(pages.totalCount);
        };

        const handleChangeRowsPerPage = async (
            event: React.ChangeEvent<HTMLInputElement>
        ) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
            setskipKeyQueue([]);
            const pages = await getPagesDraft(status, event.target.value);
            setDraftVersionList([...pages.list]);
            setTotalCount(pages.totalCount);
        };

        const pushKey = (key: any, queue: any = skipKeyQueue) => {
            const skipValue = [...queue, key];
            setskipKeyQueue(skipValue);
        };

        const popKey = (queue: any[] = skipKeyQueue) => {
            const skipValue = [...queue];
            const popValue = skipValue.pop();
            setskipKeyQueue(skipValue);
            return skipValue;
        };

        const popTwice = () => {
            const queue1 = popKey();
            const queue = popKey(queue1);
            return queue;
        };

        const onHoverDate = (row: any, isEnter: boolean) => {
            if (isEnter && dateDifference(row.publishDate) < 31) {
                setHoverId(row.id);
            } else {
                setHoverId(null);
            }
        };

        const showDateBasedOnStatus = (row: any) => {
            if (status == DRAFT_STATUS) {
                return (
                    <TableCell>
                        Saved On <br />
                        {MILLIS_TO_LOCALE_DIGIT_FORMAT(row.lastModified)}{" "}
                        {timezoneCode}
                    </TableCell>
                );
            } else if (status == SCHEDULED_STATUS) {
                return (
                    <TableCell>
                        <Tooltip
                            open={row.id == hoverId}
                            placement="top"
                            title={
                                <CountdownTimer
                                    targetDate={new Date(
                                        row.publishTime
                                    ).getTime()}
                                />
                            }
                            arrow
                        >
                            <p
                                onMouseEnter={() => onHoverDate(row, true)}
                                onMouseLeave={() => onHoverDate(row, false)}
                            >
                                Scheduled for <br />
                                {LOCAL_PST_DATE_CONVERSION(
                                    row.publishDate,
                                    row.publishTime
                                )}{" "}
                                {dayjs(row.publishTime).format("hh:mm A")}{" "}
                                {timezoneCode}
                            </p>
                        </Tooltip>
                    </TableCell>
                );
            } else if (status == LIVE) {
                return (
                    <TableCell>
                        Published On <br />
                        {LOCAL_PST_DATE_CONVERSION(
                            row.publishDate,
                            row.publishTime
                        )}{" "}
                        {dayjs(row.publishTime).format("hh:mm A")}{" "}
                        {timezoneCode}
                    </TableCell>
                );
            }
        };

        const clonePage = (row: any) => {
            setSelectedPage(row);
            setVersionPopup(true);
        };

        return (
            <div>
                <div>
                    <h4 className="text-capitalize">{status}</h4>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                            <TableHead>{tableRowRender()}</TableHead>
                            <TableBody>
                                {draftVersionList &&
                                    draftVersionList.map(
                                        (row: any, index: number) => (
                                            <TableRow
                                                key={row.id}
                                                sx={{
                                                    "&:last-child td, &:last-child th":
                                                        { border: 0 },
                                                }}
                                            >
                                                <TableCell>
                                                    <h4> {row.versionName}</h4>
                                                    {row?.canonicalName || ""}
                                                </TableCell>
                                                <TableCell>
                                                    {
                                                        masterInformation[
                                                            row?.template
                                                        ]
                                                    }
                                                </TableCell>
                                                <TableCell className="text-capitalize">
                                                    {row?.publishStatus || "-"}
                                                </TableCell>

                                                {showDateBasedOnStatus(row)}

                                                <TableCell
                                                    width={150}
                                                    align="center"
                                                >
                                                    {status == LIVE && (
                                                        <Tooltip title="View Page Configuration">
                                                            <VisibilityIcon
                                                                className="action-icons cursor-pointer"
                                                                onClick={() =>
                                                                    handlePageConfiguration(
                                                                        row.id
                                                                    )
                                                                }
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    {showActionButton &&
                                                        status ==
                                                            DRAFT_STATUS && (
                                                            <Tooltip title="Edit Page">
                                                                <EditIcon
                                                                    className="action-icons cursor-pointer"
                                                                    onClick={() =>
                                                                        handlePageConfiguration(
                                                                            row.id
                                                                        )
                                                                    }
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    {hasPermission(
                                                        PERMISSIONS.curation,
                                                        ACTION.EDIT
                                                    ) &&
                                                        showActionButton &&
                                                        status ==
                                                            SCHEDULED_STATUS && (
                                                            <Tooltip title="Cancel Page">
                                                                <CancelIcon
                                                                    className="action-icons cursor-pointer"
                                                                    onClick={() =>
                                                                        handleCancelPage(
                                                                            row
                                                                        )
                                                                    }
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    {hasPermission(
                                                        PERMISSIONS.curation,
                                                        ACTION.EDIT
                                                    ) && (
                                                        <Tooltip title="Clone Page">
                                                            <FileCopyIcon
                                                                className="action-icons cursor-pointer"
                                                                onClick={() =>
                                                                    clonePage(
                                                                        row
                                                                    )
                                                                }
                                                            />
                                                        </Tooltip>
                                                    )}

                                                    {hasPermission(
                                                        PERMISSIONS.curation,
                                                        ACTION.EDIT
                                                    ) &&
                                                        showActionButton && (
                                                            <Tooltip title="Delete Page">
                                                                <DeleteIcon
                                                                    className="action-icons cursor-pointer"
                                                                    onClick={() =>
                                                                        openDeleteModal(
                                                                            row
                                                                        )
                                                                    }
                                                                />
                                                            </Tooltip>
                                                        )}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </div>
                <DeleteModal
                    title="Delete Page?"
                    isOpen={isDeleteModalOpen}
                    handleClose={closeDeleteModal}
                    handleConfirm={deleteTheObject}
                >
                    <p>
                        Are you sure you want to delete{" "}
                        {selectedPage?.versionName}?
                    </p>
                </DeleteModal>

                <DeleteModal
                    title="Cancel Page?"
                    isOpen={showCancelPopup}
                    handleClose={closeCancelModal}
                    handleConfirm={handleMoveToDraft}
                    cancelLabel="No"
                    confirmLabel="Yes"
                >
                    <p>
                        Are you sure you want to move this version '
                        <b>{selectedPage?.versionName}</b>' to drafts?
                    </p>
                </DeleteModal>

                {versionPopup && selectedPage && (
                    <CloneContentVersion
                        formData={selectedPage}
                        show={versionPopup}
                        handleCloseCB={handleClosePopup}
                    />
                )}
            </div>
        );
    }
);

export default ContentCurationVersionTable;
